import { path } from 'd3-path'
import Symbol from '../symbol.js'
import * as colors from 'scoreboard-colors'

function intersection (line1, line2) {
  const [[x1, y1], [x2, y2]] = line1
  const [[x3, y3], [x4, y4]] = line2

  // Coefficients of the lines
  const a1 = y2 - y1
  const b1 = x1 - x2
  const c1 = a1 * x1 + b1 * y1

  const a2 = y4 - y3
  const b2 = x3 - x4
  const c2 = a2 * x3 + b2 * y3

  const determinant = a1 * b2 - a2 * b1

  const x = (b2 * c1 - b1 * c2) / determinant
  const y = (a1 * c2 - a2 * c1) / determinant

  return [x, y]
}

export default class Page extends Symbol {

  handles = [{
    defaults: [.66, 1], // TODO this is going to be tricky. Probably a combination of pixels and percentages
    constraints: (dimensions) => {
      const max = 1 - 1 / dimensions.width
      const sideLength = Math.min(dimensions.width / 2, dimensions.height / 2)
      const min = Math.max(.5, 1 - sideLength / dimensions.width)

      return [[min, max], null]
    },
    cursor: 'ew-resize'
  }]

  getPaths (data, dimensions, handles) {
    const color = data.color
    const foldColor = colors.adjustLightness(color, 35) // Solid fill, folded piece lighter

    const id0 = `${this.id}-0-component`
    const id1 = `${this.id}-1-component`

    const offset = this.getFlipWidth(dimensions, handles)
    const outline = this.outline(dimensions, data.strokeWidth, offset)

    let clip = {
      id: `${this.id}-clip`,
      path: outline,
    }

    // The first component is the main sheet
    return [{
      path: this.main(dimensions, offset),
      id: id0,
      fill: this.getFillValue(id0, data.fill, color),
      clip,
      gradient: {
        type: 'linearGradient',
        id: `shape-gradient-0-${this.id}`,
        x1: '0%',
        y1: '0%',
        x2: '0%',
        y2: '100%',
        stops: [
          { offset: '0%', color: colors.adjustLightness(color, 35) },
          { offset: '50%', color: color },
          { offset: '100%', color: colors.adjustLightness(color, -7) },
        ]
      }
    }, {
      // The second component is the corner fold
      path: this.fold(dimensions, offset),
      id: id1,
      fill: this.getFillValue(id1, data.fill, foldColor),
      clip,
      gradient: {
        type: 'linearGradient',
        id: `shape-gradient-1-${this.id}`,
        x1: '0%',
        y1: '0%',
        x2: '100%',
        y2: '100%',
        stops: [
          { offset: '0%', color: colors.adjustLightness(color, 50) },
          { offset: '70%', color: colors.adjustLightness(color, -10) },
        ]
      }
    }, {
      path: outline,
      fill: 'none',
      styles: {
        'stroke': 'none', // overwrite it
      }
    }]
  }

  getFlipWidth (dimensions, handles) {
    return dimensions.width - dimensions.width * handles[0][0]
  }

  getFlattenedPaths (data, dimensions) {
    // When flattened, the page is a rectangle, not showing the bottom tip
    const paths = this.getPaths(data, dimensions, [[.9999999, 1]]) // Handle is at the bottom right corner
    paths.at(-1).fill = paths.at(0).fill
    return paths
  }

  outline (dimensions, stroke, offset) {
    const _path = path()
    const strokeOffset = stroke / 2

    _path.moveTo(-strokeOffset, -strokeOffset)
    _path.lineTo(dimensions.width + strokeOffset, -strokeOffset)
    _path.lineTo(dimensions.width + strokeOffset, dimensions.height - offset)
    _path.lineTo(dimensions.width - offset + strokeOffset, dimensions.height + strokeOffset)
    _path.lineTo(-strokeOffset, dimensions.height + strokeOffset)
    _path.closePath()

    return _path.toString()
  }

  foldedTip (a, b) {
    // Calculate the midpoint of ab
    const midpoint = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
    // Determine the original distance from the midpoint to the bottom corner
    const distance = Math.sqrt((b[0] - midpoint[0]) ** 2 + (a[1] - midpoint[1]) ** 2)

    // Now we need to calculate the new point
    const l = b[0] - a[0]
    const magnitude = l / Math.sqrt(l ** 2 + l ** 2)

    return [
      midpoint[0] + magnitude * distance * -0.8,
      midpoint[1] + magnitude * distance * -0.8
    ]
  }

  fold (dimensions, offset) {
    const _path = path()

    const a = [dimensions.width - offset, dimensions.height]
    const b = [dimensions.width, dimensions.height - offset]
    const c = this.foldedTip(a, b)

    _path.moveTo(...a)
    _path.lineTo(...c)
    _path.lineTo(...b)
    _path.closePath()

    return _path.toString()
  }

  main (dimensions, offset) {
    const _path = path()

    _path.moveTo(0, 0)
    _path.lineTo(dimensions.width, 0)
    _path.lineTo(dimensions.width, dimensions.height - offset)
    _path.lineTo(dimensions.width - offset, dimensions.height)
    _path.lineTo(0, dimensions.height)
    _path.closePath()

    return _path.toString()
  }

  textContainer (dimensions, handles) {
    const offset = this.getFlipWidth(dimensions, handles)
    const width = dimensions.width - offset
    const height = dimensions.height - offset

    if (width > height) {
      return {
        center: [width / 2, dimensions.center[1]],
        width,
        height: dimensions.height
      }
    } else {
      return {
        center: [dimensions.center[0], height / 2],
        width: dimensions.width,
        height
      }
    }
  }
}
